/* eslint-disable react/no-unescaped-entities */
/**
 * /venue-signup/:token — the page a venue owner lands on after their
 * one-tap link from the admin reimbursements screen.  Shows the
 * cross-redemption clauses (7-10) preview, collects BSB / account name
 * / account number, and on submit POSTS to
 * /api/venue-signup/{token}/accept, which stamps the bank details onto
 * the businesses row, marks the token consumed, and releases the
 * 14-day payout cycle.
 *
 * Backend endpoints:
 *   GET  /api/venue-signup/{token}        → fetch invite shape
 *   POST /api/venue-signup/{token}/accept → submit + finalise
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Banknote, ShieldCheck, CheckCircle2,
         Users, Repeat, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

export default function VenueSignupPage() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading");   // loading|ready|submitting|done|error
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  // form state
  const [bsb, setBsb] = useState("");
  const [acctName, setAcctName] = useState("");
  const [acctNum, setAcctNum] = useState("");
  const [abn, setAbn] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [signedRole, setSignedRole] = useState("");
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    api.get(`/api/venue-signup/${token}`).then(r => {
      setInvite(r.data);
      if (r.data.already_signed) {
        setPhase("done");
      } else {
        if (r.data.owner_name) setSignedBy(r.data.owner_name);
        setPhase("ready");
      }
    }).catch(e => {
      setError(e?.response?.data?.detail || e.message);
      setPhase("error");
    });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setPhase("submitting");
    try {
      const r = await api.post(`/api/venue-signup/${token}/accept`, {
        bsb: bsb.replace(/\s|-/g, ""),
        account_name: acctName.trim(),
        account_number: acctNum.replace(/\s|-/g, ""),
        abn: abn || null,
        signed_by: signedBy.trim(),
        signed_by_role: signedRole.trim() || null,
        accepted_clauses: accepted,
      });
      setInvite(prev => ({ ...prev, ...r.data, already_signed: true }));
      setPhase("done");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setPhase("ready");
    }
  };

  if (phase === "loading") {
    return (
      <div style={s.root}>
        <div style={s.loadingCard} data-testid="venue-signup-loading">
          <Loader2 size={32} className="spin" />
          <p>Loading your invite…</p>
        </div>
        <style>{spin}</style>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div style={s.root}>
        <div style={{ ...s.card, textAlign: "center" }} data-testid="venue-signup-error">
          <h2 style={s.h2}>Hmm — that link didn't work</h2>
          <p style={s.errBody}>{error}</p>
          <p style={s.sub}>
            Please reach out to the Hustle team for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div style={s.root}>
        <div style={s.card} data-testid="venue-signup-done">
          <div style={s.doneBadge}><CheckCircle2 size={40} /></div>
          <h2 style={s.h2}>Welcome to the network</h2>
          <p style={s.sub}>
            Signed and on file — bank details captured, agreement
            accepted (v3.0 with the new cross-redemption clauses).
            Reimbursements release on the next 14-day cycle and roll
            straight into the BSB/account you submitted.
          </p>
          {invite?.signed_at && (
            <div style={s.signedTag}>
              Signed {new Date(invite.signed_at).toLocaleString("en-AU")}
            </div>
          )}
          <p style={{ ...s.sub, marginTop: 28 }}>
            You can close this tab — we'll email a copy of the agreement
            to <strong>{invite.email || "your email on file"}</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.root} data-testid="venue-signup-root">
      <header style={s.header}>
        <div style={s.brand}>Hustle<span style={{ color: "#fbbf24" }}>Eats</span></div>
        <div style={s.brandSub}>Restaurant Agreement · v3.0</div>
      </header>

      <div style={s.card}>
        <h1 style={s.h1}>
          {invite.biz_name ? `Welcome, ${invite.biz_name}` : "Activate your payout account"}
        </h1>
        <p style={s.sub}>
          Two-minute setup. Review the four new cross-redemption clauses
          below, drop in your BSB and account number, and you're on the
          14-day payout cycle from your very next Hustle Dollars
          redemption.
        </p>

        {/* ── Clauses preview ─────────────────────────────────── */}
        <section style={s.clausesBlock}>
          <div style={s.clausesBadge}>
            <ShieldCheck size={14} /> Cross-Redemption Network · Clauses 7–10
          </div>

          <Clause icon={Banknote} title="7. Hustle's liability for platform credits">
            When a customer redeems Hustle Dollars at your venue, Hustle
            accepts the credit as cash — the full amount becomes an
            accounts-payable obligation from Hustle to you, recorded in
            your admin under "Reimbursements". <strong>You are never out
            of pocket for accepting Hustle Dollars.</strong>
          </Clause>

          <Clause icon={Users} title="8. Customer acquisition moat">
            Every redemption and every in-venue HustleApp visit hands you
            the customer's contact details (name, email, mobile, dietary
            tags, last-visit date) into <strong>your own CRM</strong>,
            licensed to you in perpetuity for direct marketing.
          </Clause>

          <Clause icon={Repeat} title="9. Network reciprocity">
            Your own issued credits (refunds, loyalty top-ups, gift
            cards) spend across the HustleApp network too — pulling your
            diners back into the wider ecosystem and keeping your brand
            top-of-mind at every touch-point during their week.
          </Clause>

          <Clause icon={FileText} title="10. Reimbursement schedule">
            HD$ redemptions are paid out by EFT on a 14-day rolling
            cycle, referenced against the line-items in your
            "Reimbursements" tab. Disputed line-items must be raised
            within 30 days of payout reference date.
          </Clause>

          <a href="/restaurant-agreement" target="_blank" rel="noreferrer"
             style={s.fullLink} data-testid="venue-signup-full-agreement">
            Read the full Restaurant Agreement →
          </a>
        </section>

        {/* ── Form ────────────────────────────────────────────── */}
        <form onSubmit={submit} style={s.form} data-testid="venue-signup-form">
          <h3 style={s.h3}>Bank details — where Hustle pays you</h3>

          <div style={s.row2}>
            <div style={s.field}>
              <label style={s.label}>BSB</label>
              <input
                required
                type="text"
                placeholder="123-456"
                maxLength={7}
                value={bsb}
                onChange={(e) => setBsb(e.target.value)}
                style={s.input}
                data-testid="venue-signup-bsb"
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>ABN (optional)</label>
              <input
                type="text"
                placeholder="11 222 333 444"
                maxLength={14}
                value={abn}
                onChange={(e) => setAbn(e.target.value)}
                style={s.input}
                data-testid="venue-signup-abn"
              />
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Account name</label>
            <input
              required
              type="text"
              placeholder="e.g. Honey Salt Bistro Pty Ltd"
              value={acctName}
              onChange={(e) => setAcctName(e.target.value)}
              style={s.input}
              data-testid="venue-signup-acct-name"
            />
          </div>

          <div style={s.field}>
            <label style={s.label}>Account number</label>
            <input
              required
              type="text"
              placeholder="e.g. 1234 5678"
              maxLength={12}
              value={acctNum}
              onChange={(e) => setAcctNum(e.target.value)}
              style={s.input}
              data-testid="venue-signup-acct-num"
            />
          </div>

          <h3 style={{ ...s.h3, marginTop: 28 }}>Signed by</h3>
          <div style={s.row2}>
            <div style={s.field}>
              <label style={s.label}>Your full name</label>
              <input
                required
                type="text"
                value={signedBy}
                onChange={(e) => setSignedBy(e.target.value)}
                style={s.input}
                data-testid="venue-signup-signedby"
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Role (Owner / Manager / Director)</label>
              <input
                type="text"
                placeholder="e.g. Owner"
                value={signedRole}
                onChange={(e) => setSignedRole(e.target.value)}
                style={s.input}
                data-testid="venue-signup-role"
              />
            </div>
          </div>

          <label style={s.checkbox} data-testid="venue-signup-accept-checkbox-wrap">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="venue-signup-accept-checkbox"
            />
            <span>
              I accept the HustleEats Restaurant Agreement (v3.0,
              including the Cross-Redemption clauses 7–10 shown above)
              on behalf of <strong>{invite.biz_name || "my venue"}</strong>.
            </span>
          </label>

          {error && <div style={s.errBody}>{error}</div>}

          <button
            type="submit"
            disabled={!accepted || phase === "submitting"}
            style={{
              ...s.submitBtn,
              opacity: (!accepted || phase === "submitting") ? 0.5 : 1,
            }}
            data-testid="venue-signup-submit"
          >
            {phase === "submitting"
              ? <><Loader2 size={18} className="spin" /> Signing…</>
              : <><CheckCircle2 size={18} /> Sign &amp; activate payouts</>}
          </button>
          <p style={s.subtle}>
            One-time link, expires{" "}
            {invite.expires_at && new Date(invite.expires_at).toLocaleDateString("en-AU")}.
            Bank details are encrypted at rest.
          </p>
        </form>
      </div>
      <style>{spin}</style>
    </div>
  );
}

function Clause({ icon: Icon, title, children }) {
  return (
    <div style={s.clause}>
      <div style={s.clauseTitle}>
        <Icon size={16} />
        <strong>{title}</strong>
      </div>
      <p style={s.clauseBody}>{children}</p>
    </div>
  );
}

const spin = `.spin { animation: spin 1s linear infinite; }
              @keyframes spin { to { transform: rotate(360deg); } }`;

const s = {
  root: { minHeight: "100vh", background: "#f4f4f0", padding: "32px 16px",
          fontFamily: "Inter, system-ui, sans-serif", color: "#0f172a" },
  header: { maxWidth: 720, margin: "0 auto 24px", display: "flex",
            justifyContent: "space-between", alignItems: "baseline" },
  brand: { fontFamily: "Georgia, serif", fontSize: 24, fontWeight: 800,
           letterSpacing: "-0.02em" },
  brandSub: { fontSize: 12, color: "#64748b", letterSpacing: "0.1em",
              textTransform: "uppercase" },
  card: { maxWidth: 720, margin: "0 auto", background: "white",
          borderRadius: 16, padding: 40,
          boxShadow: "0 20px 60px rgba(0,0,0,0.06)" },
  loadingCard: { display: "flex", alignItems: "center", gap: 16,
                 padding: 60, maxWidth: 400, margin: "120px auto",
                 background: "white", borderRadius: 14,
                 justifyContent: "center", color: "#475569" },
  h1: { fontSize: 28, fontWeight: 800, margin: "0 0 8px",
        letterSpacing: "-0.02em" },
  h2: { fontSize: 24, fontWeight: 800, margin: "0 0 8px",
        letterSpacing: "-0.02em" },
  h3: { fontSize: 14, fontWeight: 800, letterSpacing: "0.06em",
        textTransform: "uppercase", color: "#64748b",
        margin: "20px 0 12px" },
  sub: { color: "#475569", fontSize: 14, lineHeight: 1.55, margin: 0 },
  clausesBlock: { background: "#fffbeb", border: "1px solid #fde68a",
                  borderRadius: 12, padding: 24, margin: "28px 0 32px" },
  clausesBadge: { display: "inline-flex", alignItems: "center", gap: 6,
                  background: "#0f172a", color: "#fbbf24",
                  padding: "5px 12px", borderRadius: 999, fontSize: 11,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                  fontWeight: 700, marginBottom: 16 },
  clause: { borderTop: "1px solid #fde68a", padding: "14px 0" },
  clauseTitle: { display: "flex", alignItems: "center", gap: 8,
                 marginBottom: 6, color: "#92400e", fontSize: 14 },
  clauseBody: { margin: 0, fontSize: 13, color: "#1f2937",
                lineHeight: 1.6 },
  fullLink: { display: "inline-block", marginTop: 12, color: "#0ea5e9",
              fontSize: 13, textDecoration: "none", fontWeight: 600 },
  form: { display: "flex", flexDirection: "column", gap: 4 },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  field: { display: "flex", flexDirection: "column", gap: 4,
           marginBottom: 12 },
  label: { fontSize: 12, fontWeight: 700, color: "#64748b",
           textTransform: "uppercase", letterSpacing: "0.06em" },
  input: { padding: "12px 14px", border: "1px solid #e2e8f0",
           borderRadius: 10, fontSize: 15, outline: "none",
           fontFamily: "inherit" },
  checkbox: { display: "flex", alignItems: "start", gap: 10,
              marginTop: 16, padding: 16, background: "#f8fafc",
              border: "1px solid #e2e8f0", borderRadius: 10,
              fontSize: 13, lineHeight: 1.5, cursor: "pointer" },
  submitBtn: { marginTop: 20, padding: "16px 20px", background: "#0f172a",
               color: "white", border: "none", borderRadius: 12,
               fontWeight: 700, fontSize: 16, cursor: "pointer",
               display: "inline-flex", alignItems: "center",
               justifyContent: "center", gap: 8 },
  subtle: { fontSize: 11, color: "#94a3b8", margin: "10px 0 0",
            textAlign: "center" },
  errBody: { background: "#fee2e2", color: "#991b1b", padding: "10px 14px",
             borderRadius: 8, fontSize: 13, marginTop: 12 },
  doneBadge: { width: 72, height: 72, borderRadius: "50%",
               background: "#dcfce7", color: "#16a34a",
               display: "inline-flex", alignItems: "center",
               justifyContent: "center", marginBottom: 16 },
  signedTag: { display: "inline-block", marginTop: 20, padding: "6px 14px",
               background: "#f1f5f9", borderRadius: 999, fontSize: 13,
               color: "#475569", fontWeight: 600 },
};
